<template>
  <v-container
    id="view-credit-billing-bills"
    fluid
    v-if="loaded"
  >
    <v-row>
      <v-col cols="12">
        <Breadcrumb :items="items" ></Breadcrumb>
        <h1>
          All {{ accountData.development }} Accounts
        </h1>

      </v-col>

      <v-col cols="12" class="account-overview">
        <v-row>
          <header-block title="All Accounts" :content="String(accountData.accounts.length)"></header-block>
          <header-block title="In Debit Accounts" :content="String(accountData.accounts.filter(account => account.balance.is_negative == true).length)"></header-block>
          <header-block v-if="accountData.global_sage" title="Void Account" :content="String(accountData.global_sage)"></header-block>
          <header-block v-if="accountData.global_sage && accountData.global_sage_balance.balance" :color="getVoidBalanceColor" title="Void Account Balance" :content="String('£' + accountData.global_sage_balance.balance)"></header-block>
        </v-row>
      </v-col>


      <v-col cols="12">
        <h2>Exports</h2>
        <vue-json-to-csv ref="allDownload" :csv-title="getDownloadName(false, false)" :labels="downloads" :json-data="accountDownload(false)"><span></span>
        </vue-json-to-csv>
        <vue-json-to-csv ref="debitDownload" :csv-title="getDownloadName(true, false)" :labels="downloads" :json-data="accountDownload(true)"><span></span>
        </vue-json-to-csv>
        <vue-json-to-csv ref="vulnerabilityDownload" :csv-title="getDownloadName(false, true)" :labels="vulnerableApartmentDownloads" :json-data="accountData.vulnerable_apartments"><span></span>
        </vue-json-to-csv>
        <v-btn
          :disabled="accountData.accounts.length === 0"
          @click="downloadAll"
          class="ma-2"
          color="green"  role="link">
          Export All
        </v-btn>

        <v-btn
          :disabled="accountData.accounts.filter(account => account.balance.is_negative == true).length === 0"
          @click="downloadDebit"
          class="ma-2"
          color="green"  role="link">
          Export In Debit Accounts
        </v-btn>

        <v-btn
          :disabled="accountData.vulnerable_apartments.length === 0"
          @click="downloadVulnerabilities"
          class="ma-2"
          color="green"  role="link">
          Vulnerable Apartments
        </v-btn>
      </v-col>


      <v-col cols="12">
        <h2>Accounts List</h2>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-spacer></v-spacer>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>

          </v-card-title>

          <v-card-text style="text-align: right">
            <strong>Quick Filter</strong>
            <v-btn
              :disabled="filterType === 'debit'"
              @click="setFilter('debit')"
              class="ma-2"
              color="blue"  role="link">
              Only in debit
            </v-btn>
            <v-btn
              :disabled="filterType === 'all'"
              @click="setFilter('all')"
              class="ma-2"
              color="blue"  role="link">
              Show all
            </v-btn>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="accountFilteredData"
            :search="search"
            item-key="id"
            :items-per-page=100
            :footer-props="listSize"
            class="elevation-1"
          >


            <template #item.balance.balance="{ value }">
              {{ value }}
            </template>

            <template #item.type="{ value }">
              <span v-if="value === 'sub'">SubLease</span>
              <span v-else>Lease</span>
            </template>

            <template #item.is_active="{ value }">
              <v-icon
                class="active-check"
                v-if="value"
                right
              >
                mdi-check-circle
              </v-icon>
            </template>

          </v-data-table>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script>
import {get} from "vuex-pathify";
import Breadcrumb from "@/components/Breadcrumb";
import {date} from "@/mixins/date";
import {creditBilling} from "@/mixins/credit-billing";
import HeaderBlock from "@/views/credit-billing/generic/HeaderBlock";
import {dataTables} from "@/mixins/data-tables";
import VueJsonToCsv from 'vue-json-to-csv'

export default {
  name: "CreditBillingAccounts",
  props: {
    id: String
  },
  components: {
    Breadcrumb,
    HeaderBlock,
    VueJsonToCsv
  },
  mixins: [date, creditBilling, dataTables],
  computed: {
    getVoidBalanceColor() {
      if (this.accountData.global_sage_balance.is_negative) {
        return "#FF4136";
      }
      return "#5d3791";
    },
    accountData: get('creditBilling/developmentAccounts'),
    totalBillsSelected() {
      return this.selectedDownload.bills.length + this.selectedDownload.credits.length;
    },
    items() {
      return [
        {
          text: 'All Developments',
          disabled: false,
          exact: true,
          to: '/credit-billing',
        },
        {
          text: this.accountData.development,
          disabled: false,
          exact: true,
          to: '/credit-billing/' + this.id,
        },
        {
          text: "All Bills",
          disabled: true,
        }
      ];
    },
    accountFilteredData() {
      if (this.filterType === 'debit') {
        return this.accountData.accounts.filter(account => account.balance.is_negative == true)
      }

      return this.accountData.accounts;
    }
  },
  created() {
    this.$store.dispatch("loader/loaderCancel");
    this.fetchData();
  },
  data: () => ({
    filterType: "all",
    loaded: true,
    downloadLoading: false,
    selectedDownload: {
      credits: [],
      bills: []
    },
    listSize: {
      "items-per-page-options": [10, 25, 50, 100]
    },
    search: '',
    headers: [
      {
        text: 'Account Ref',
        align: 'start',
        value: 'sage',
      },
      {text: "Owner", value: 'type'},
      {text: "Account", value: 'account'},
      {text: "Apartment", value: 'apartments'},
      {text: 'Active', value: 'is_active'},
      {text: 'Balance', value: 'balance.balance'},
    ],
    downloads: {
      account: { title: 'Account' },
      owner: { title: 'Owner' },
      sage: { title: 'Account Ref' },
      apartments: { title: 'Apartments' },
      balance: { title: 'Balance' },
      is_active: { title: 'Active Account' },
      sage_updated: { title: 'Balance Updated' }
    },
    vulnerableApartmentDownloads: {
      apartment: { title: 'Apartment' },
      lease_sage: { title: 'Lease Sage' },
      sub_lease_sage: { title: 'SubLease Sage' },
      lease_vulnerable: { title: 'Lease Vulnerable' },
      lease_vulnerable_reason: { title: 'Lease Vulnerable Reason' },
      lease_vulnerable_contact: { title: 'Lease Vulnerable Contact' },
      lease_vulnerable_phone: { title: 'Lease Vulnerable Phone' },
      sub_lease_vulnerable: { title: 'SubLease Vulnerable' },
      sub_lease_vulnerable_reason: { title: 'SubLease Vulnerable Reason' },
      sub_lease_vulnerable_contact: { title: 'SubLease Vulnerable Contact' },
      sub_lease_vulnerable_phone: { title: 'SubLease Vulnerable Phone' }
    }
}),
  methods: {
    getDownloadName(isDebit, isVulnerableApartments) {
      if (isDebit) {
        return this.stringToSlug(this.accountData.development) + "_debit-accounts";
      }

      if (isVulnerableApartments) {
        return this.stringToSlug(this.accountData.development) + "_vulnerable-apartments";
      }

      return this.stringToSlug(this.accountData.development) + "_all-accounts";
    },
    accountDownload(isDebitOnly) {
      let data = [];

      if (this.accountData.global_sage && (this.accountData.global_sage_balance.is_negative || !isDebitOnly)) {
        data.push({
          account: "Void Account",
          owner: "",
          sage: this.accountData.global_sage,
          apartments: "",
          balance: this.accountData.global_sage_balance.balance,
          is_active: "Yes",
          sage_updated: "",
        });
      }

      this.accountData.accounts.forEach((account) => {
        const type = account.type === 'lease' ? "Lease" : "SubLease";
        if (!isDebitOnly || account.balance.is_negative) {
          data.push({
            account: account.account,
            owner: type,
            sage: account.sage,
            apartments: account.apartments,
            balance: account.balance.balance,
            is_active: account.is_active ? "Yes" : "",
            sage_updated: account.sage_updated_at,
          });
        }
      });

      return data;
    },
    downloadAll() {
      this.$nextTick(() => {
        this.$refs.allDownload.$el.click();
      });
    },
    downloadDebit() {
      this.$nextTick(() => {
        this.$refs.debitDownload.$el.click();
      });
    },
    downloadVulnerabilities() {
      this.$nextTick(() => {
        this.$refs.vulnerabilityDownload.$el.click();
      });
    },
    setFilter(type) {
      this.filterType = type;
    },
    async fetchData() {
      await this.$store.dispatch("loader/loaderInitiate", {title: "Loading Development Accounts"});


      const success = await this.$store.dispatch("creditBilling/getDevelopmentAccounts", {
        developmentRef: this.id
      });

      if (!success) {
        await this.$router.push("/credit-billing/" + this.id);
        return;
      }

      this.loaded = true;

      await this.$store.dispatch("loader/loaderCancel");

    }
  }
}
</script>

<style lang="scss" scoped>
.warn p {
  margin-bottom: 0px;
  font-size: 0.9rem;
}
</style>
